/**
 * Medical Leaves Apply Page
 * Select employee and apply medical leave
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Alert,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { PageShell, DataTable, PrerequisiteBlock, type DataTableColumn } from '../../components';
import { CompanySelectField } from '../../components/forms/CompanySelectField';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import employeeService from '../../services/employeeService';
import { Employee, EmployeeStatus } from '../../types/employee';
import { EmployeeForLeave } from '../../types/medicalLeave';
import { MedicalLeavesFormDialog } from './components';

const columns: DataTableColumn<Employee>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (employee) => (
      <Typography variant="body2" fontWeight="medium">
        {employee.name}
      </Typography>
    ),
  },
  {
    id: 'cpf',
    label: 'CPF',
    render: (employee) => (
      <Typography variant="body2" fontFamily="monospace">
        {employee.cpf}
      </Typography>
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (employee) => (
      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
        {employee.company?.legalName || employee.company?.tradeName || '-'}
      </Typography>
    ),
  },
  {
    id: 'department',
    label: 'Setor',
    render: (employee) => (
      <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
        {employee.department?.name || '-'}
      </Typography>
    ),
  },
  {
    id: 'occupation',
    label: 'Cargo',
    render: (employee) => (
      <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
        {employee.occupation?.name || '-'}
      </Typography>
    ),
  },
];

export const MedicalLeavesApplyPage: React.FC = () => {
  const navigate = useNavigate();
  const { effectiveCompanyIdString, shouldShowCompanyField } = useCompanyContext();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeForLeave | null>(null);
  // Funcionário sem empresa: a licença é registrada sob a empresa do funcionário, então o
  // formulário nem abre — mesmo que a listagem, filtrada por empresa, dificilmente traga um.
  const [funcionarioSemEmpresa, setFuncionarioSemEmpresa] = useState<Employee | null>(null);

  // Auto-fill company when not showing company field
  useEffect(() => {
    if (effectiveCompanyIdString && !companyId) {
      setCompanyId(effectiveCompanyIdString);
    }
  }, [effectiveCompanyIdString, companyId]);

  // Load employees when company or filters change
  useEffect(() => {
    if (companyId) {
      loadEmployees();
    }
  }, [companyId, page, rowsPerPage]);

  const loadEmployees = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await employeeService.getEmployees({
        page: page + 1,
        limit: rowsPerPage,
        filters: {
          companyId: [Number(companyId)],
          search: search || undefined,
          status: [EmployeeStatus.ATIVO],
        },
      });

      setEmployees(response.data);
      setTotal(response.pagination.total);
    } catch (err: unknown) {
      console.error('Error loading employees:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadEmployees();
  };

  const handleCompanyChange = (value: string | null) => {
    setCompanyId(value);
    setEmployees([]);
    setPage(0);
  };

  const handleSelectEmployee = (employee: Employee) => {
    if (!employee.company) {
      setFuncionarioSemEmpresa(employee);
      return;
    }

    const employeeForLeave: EmployeeForLeave = {
      id: employee.id,
      name: employee.name,
      cpf: employee.cpf,
      companyId: employee.companyId,
      companyName: employee.company?.legalName || employee.company?.tradeName || '',
      departmentName: employee.department?.name || '',
      occupationName: employee.occupation?.name || '',
    };
    setSelectedEmployee(employeeForLeave);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedEmployee(null);
  };

  const handleDialogSuccess = () => {
    navigate('/medical-leaves');
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Licenças Médicas', path: '/medical-leaves' },
        { label: 'Aplicar Licença' },
      ]}
      title="Aplicar Licença Médica"
      subtitle="Selecione o funcionário para aplicar a licença médica"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Selecionar Funcionário" />
        <CardContent>
          <Grid container spacing={2}>
            {shouldShowCompanyField && (
              <Grid size={{ xs: 12, md: 4 }}>
                <CompanySelectField
                  value={companyId || ''}
                  onChange={handleCompanyChange}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: shouldShowCompanyField ? 6 : 10 }}>
              <TextField
                fullWidth
                label="Pesquisar funcionário"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Nome ou CPF do funcionário"
                disabled={!companyId}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={!companyId}
                  fullWidth
                  sx={{ py: 1.75 }}
                >
                  Buscar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Employees Table */}
      {!companyId ? (
        <Alert severity="info">
          Selecione uma empresa para visualizar os funcionários
        </Alert>
      ) : (
        <DataTable
          columns={columns}
          rows={employees}
          getRowId={(employee) => employee.id}
          loading={loading}
          emptyState={{
            title: 'Nenhum funcionário encontrado',
            description:
              'A busca acima lista os funcionários ativos da empresa selecionada.',
          }}
          actions={[
            {
              icon: <AddIcon fontSize="small" />,
              tooltip: 'Aplicar Licença',
              color: 'primary',
              onClick: handleSelectEmployee,
            },
          ]}
          pagination={{
            page,
            rowsPerPage,
            totalCount: total,
            onPageChange: setPage,
            onRowsPerPageChange: (value) => {
              setRowsPerPage(value);
              setPage(0);
            },
          }}
        />
      )}

      {/* Medical Leave Form Dialog */}
      <MedicalLeavesFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        employee={selectedEmployee}
      />

      <Dialog open={Boolean(funcionarioSemEmpresa)} onClose={() => setFuncionarioSemEmpresa(null)}>
        <DialogTitle>Aplicar Licença Médica</DialogTitle>
        <DialogContent>
          <PrerequisiteBlock
            title="Este funcionário não está associado a uma empresa"
            reason="A licença médica é registrada sob a empresa do funcionário, e ele ainda não tem uma."
            resolve={{
              label: 'Editar funcionário',
              permission: 'employee.update',
              mode: 'navigate',
              route: `/employees/${funcionarioSemEmpresa?.id}/edit`,
            }}
            deniedHint="Peça a um administrador para associar este funcionário a uma empresa."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFuncionarioSemEmpresa(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default MedicalLeavesApplyPage;
